import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";

export const DuplicateBudgetModal = ({ isOpen, onClose, budgetName, onAccept }) => {
  const [name, setName] = useState(budgetName || "");
  const [copyValues, setCopyValues] = useState(true);
  const [copyRollups, setCopyRollups] = useState(true);
  const [copyFormulas, setCopyFormulas] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    setName(budgetName || "");
  }, [isOpen, budgetName]);

  const handleClose = () => {
    onClose();
    setName("");
  };

  const handleAcceptActions = async () => {
    if (!name?.trim()) {
      toast.error(`Empty budget name.`, { position: "bottom-right" });
      return;
    }

    await onAccept({
      name: name.trim(),
      copyAll: copyValues && copyRollups && copyFormulas,
      copyValues,
      copyRollups,
      copyFormulas,
    });
  };

  const copyAllChecked = copyValues && copyRollups && copyFormulas;

  // Handle Esc key press
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && isOpen) {
        handleClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscKey);
    }
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={handleClose}
        ></div>
        <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
          &#8203;
        </span>
        <div
          className="inline-block transform rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <h3 className="text-lg font-bold mb-2">Duplicate Budget</h3>
            <div className="flex flex-col gap-1 mb-2">
              <span>Name:</span>
              <div className="flex-1">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full p-2.5 shadow-sm border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <input
                  id="copyAll"
                  type="checkbox"
                  checked={copyAllChecked}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setCopyValues(checked);
                    setCopyRollups(checked);
                    setCopyFormulas(checked);
                  }}
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <label htmlFor="copyAll" className="text-sm text-gray-900">
                  Copy all (values, rollups, formulas)
                </label>
              </div>
              <ul className="list-disc pl-6 space-y-2">
                <li className="flex items-center gap-2">
                  <input
                    id="copyValues"
                    type="checkbox"
                    checked={copyValues}
                    onChange={(e) => setCopyValues(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                  <label htmlFor="copyValues" className="text-sm text-gray-900">
                    Copy values
                  </label>
                </li>
                <li className="flex items-center gap-2">
                  <input
                    id="copyRollups"
                    type="checkbox"
                    checked={copyRollups}
                    onChange={(e) => setCopyRollups(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                  <label htmlFor="copyRollups" className="text-sm text-gray-900">
                    Copy rollups
                  </label>
                </li>
                <li className="flex items-center gap-2">
                  <input
                    id="copyFormulas"
                    type="checkbox"
                    checked={copyFormulas}
                    onChange={(e) => setCopyFormulas(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                  <label htmlFor="copyFormulas" className="text-sm text-gray-900">
                    Copy formulas
                  </label>
                </li>
              </ul>
            </div>
            <div className="py-3 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={handleAcceptActions}
              >
                Accept
              </button>
              <button
                type="button"
                className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={handleClose}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};